import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Truck, Search, Map as MapIcon, AlertCircle, RefreshCw, Send, CheckSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  subscribeDeliveries,
  addDelivery,
  DELIVERY_STATUS,
  updateDeliveryRouteOrder,
} from '../../services/deliveryService';
import DeliveryCard from '../../components/shared/DeliveryCard';
import DeliveryDetailModal from '../../components/shared/DeliveryDetailModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

const STATUS_FILTERS = [
  { id: 'all', label: 'Hepsi' },
  { id: DELIVERY_STATUS.PENDING, label: 'Adres Bekleyen' },
  { id: DELIVERY_STATUS.ADDRESS_REQUESTED, label: 'Adres İstendi' },
  { id: DELIVERY_STATUS.ADDRESS_RECEIVED, label: 'Adres Alındı' },
  { id: DELIVERY_STATUS.DELIVERED, label: 'Teslim Edildi' },
  { id: DELIVERY_STATUS.CENTER_PICKUP, label: 'Merkezden Teslim' },
  { id: DELIVERY_STATUS.SELF_PICKUP, label: 'Kendisi Alacak' },
];

export default function DeliveryManagement() {
  const { isSuperAdmin, currentUser, systemSettings } = useAuth();

  const [shares, setShares] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState('list');
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [adding, setAdding] = useState(null);
  const [startPoint, setStartPoint] = useState('Hasımoğlu Çiftliği, İstanbul');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    let sharesLoaded = false;
    let deliveriesLoaded = false;
    const setIfBoth = () => {
      if (sharesLoaded && deliveriesLoaded) setLoading(false);
    };

    const sharesUnsub = onSnapshot(query(collection(db, 'shares')), (snap) => {
      setShares(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      sharesLoaded = true;
      setIfBoth();
    });

    const deliveriesUnsub = subscribeDeliveries((data) => {
      setDeliveries(data);
      deliveriesLoaded = true;
      setIfBoth();
    });

    return () => {
      sharesUnsub();
      deliveriesUnsub();
    };
  }, []);

  const branchName = (branchId) => {
    if (!branchId) return null;
    const b = systemSettings?.systemBranches?.find((x) => x.id === branchId);
    return b ? b.name : branchId;
  };

  const shareholders = useMemo(() => {
    const map = new Map();
    shares.forEach((s) => {
      if (!s.customerPhone) return;
      const key = s.customerPhone;
      const existing = map.get(key);
      if (existing) {
        existing.shareIds.push(s.id);
        existing.shareCount += Number(s.shareWeight) || 1;
      } else {
        map.set(key, {
          customerPhone: s.customerPhone,
          customerName: `${s.customerFirstName || ''} ${s.customerLastName || ''}`.trim(),
          branch: s.branch || null,
          shareIds: [s.id],
          shareCount: Number(s.shareWeight) || 1,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.customerName.localeCompare(b.customerName, 'tr'));
  }, [shares]);

  const deliveryPhoneSet = useMemo(() => new Set(deliveries.map((d) => d.customerPhone)), [deliveries]);

  const filteredShareholders = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return shareholders.filter((s) => {
      if (deliveryPhoneSet.has(s.customerPhone)) return false;
      if (branchFilter !== 'all' && s.branch !== branchFilter) return false;
      if (!q) return true;
      return (
        s.customerName.toLocaleLowerCase('tr-TR').includes(q) ||
        s.customerPhone.includes(q)
      );
    });
  }, [shareholders, search, branchFilter, deliveryPhoneSet]);

  const filteredDeliveries = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return deliveries.filter((d) => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (branchFilter !== 'all' && d.branch !== branchFilter) return false;
      
      const isCenter = d.status === DELIVERY_STATUS.CENTER_PICKUP;
      const isSelf = d.status === DELIVERY_STATUS.SELF_PICKUP;
      if (deliveryTypeFilter === 'home' && (isCenter || isSelf)) return false;
      if (deliveryTypeFilter === 'center' && !isCenter) return false;
      if (deliveryTypeFilter === 'self' && !isSelf) return false;
      
      if (!q) return true;
      return (
        (d.customerName || '').toLocaleLowerCase('tr-TR').includes(q) ||
        (d.customerPhone || '').includes(q) ||
        (d.address || '').toLocaleLowerCase('tr-TR').includes(q)
      );
    });
  }, [deliveries, statusFilter, search, branchFilter, deliveryTypeFilter]);

  const handleAddToList = async (sh) => {
    if (deliveryPhoneSet.has(sh.customerPhone)) return;
    setAdding(sh.customerPhone);
    try {
      await addDelivery(
        {
          customerPhone: sh.customerPhone,
          customerName: sh.customerName,
          branch: sh.branch,
          shareIds: sh.shareIds,
          shareCount: sh.shareCount,
        },
        currentUser?.uid
      );
    } catch (err) {
      alert('Eklenemedi: ' + err.message);
    } finally {
      setAdding(null);
    }
  };

  const handleCenterDelivery = async (sh) => {
    if (deliveryPhoneSet.has(sh.customerPhone)) return;
    setAdding(sh.customerPhone);
    try {
      await addDelivery(
        {
          customerPhone: sh.customerPhone,
          customerName: sh.customerName,
          branch: sh.branch,
          shareIds: sh.shareIds,
          shareCount: sh.shareCount,
          isCenterDelivery: true
        },
        currentUser?.uid
      );
    } catch (err) {
      alert('Eklenemedi: ' + err.message);
    } finally {
      setAdding(null);
    }
  };

  const handleSelfPickup = async (sh) => {
    if (deliveryPhoneSet.has(sh.customerPhone)) return;
    setAdding(sh.customerPhone);
    try {
      await addDelivery(
        {
          customerPhone: sh.customerPhone,
          customerName: sh.customerName,
          branch: sh.branch,
          shareIds: sh.shareIds,
          shareCount: sh.shareCount,
          isSelfPickup: true
        },
        currentUser?.uid
      );
    } catch (err) {
      alert('Eklenemedi: ' + err.message);
    } finally {
      setAdding(null);
    }
  };

  const toggleSelect = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectedDeliveries = useMemo(
    () => deliveries.filter((d) => selectedIds.has(d.id) && d.address?.trim()),
    [deliveries, selectedIds]
  );

  const activeDeliveryLive = useMemo(() => {
    if (!activeDelivery) return null;
    return deliveries.find((d) => d.id === activeDelivery.id) || null;
  }, [activeDelivery, deliveries]);

  useEffect(() => {
    if (activeDelivery && !activeDeliveryLive) {
      setActiveDelivery(null);
    }
  }, [activeDelivery, activeDeliveryLive]);

  const handleBuildRoute = () => {
    if (selectedDeliveries.length === 0) return;
    const stops = selectedDeliveries.map((d) => encodeURIComponent((d.address || '').trim())).filter(Boolean);
    if (stops.length === 0) {
      alert("Seçilen teslimatların hiçbirinde geçerli bir adres bulunamadı.");
      return;
    }
    const start = startPoint.trim() ? encodeURIComponent(startPoint.trim()) : null;
    const path = (start ? [start, ...stops] : stops).join('/');
    const url = `https://www.google.com/maps/dir/${path}`;
    window.open(url, '_blank');
  };

  const handleSendToCourier = () => {
    if (selectedDeliveries.length === 0) return;
    
    let message = `🚗 *KURYE TESLİMAT LİSTESİ* 🚗\n\n`;
    
    selectedDeliveries.forEach((d, index) => {
      message += `*${index + 1}. SIRA*\n`;
      message += `👤 İsim: ${d.customerName || '-'} (${d.shareCount || 0} Hisse)\n`;
      message += `📞 Tel: ${d.customerPhone || '-'}\n`;
      message += `📍 Adres: ${d.address || 'Adres Girilmemiş'}\n\n`;
    });
    
    const stops = selectedDeliveries.map((d) => encodeURIComponent((d.address || '').trim())).filter(Boolean);
    if (stops.length > 0) {
      const start = startPoint.trim() ? encodeURIComponent(startPoint.trim()) : null;
      const path = (start ? [start, ...stops] : stops).join('/');
      const url = `https://www.google.com/maps/dir/${path}`;
      message += `🗺️ *Toplu Rota Linki:*\n${url}\n\n`;
    }
    
    message += `Kolay gelsin.`;
    
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = deliveries.findIndex((d) => d.id === active.id);
      const newIndex = deliveries.findIndex((d) => d.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Normalde arrayMove ile UI anında güncellenir ancak firebase snapshot gelince tekrar render olur.
        const newDeliveries = arrayMove(deliveries, oldIndex, newIndex);
        const orderedIds = newDeliveries.map((d) => d.id);
        
        try {
          await updateDeliveryRouteOrder(orderedIds);
        } catch (error) {
          alert('Sıralama güncellenemedi: ' + error.message);
        }
      }
    }
  };

  if (!isSuperAdmin) {
    return <div className="p-8 text-white">Yetersiz Yetki</div>;
  }

  return (
    <div className="p-2 md:p-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-forest-500/20 text-forest-400 rounded-xl">
          <Truck className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Eve Teslimat</h1>
          <p className="text-sm text-navy-300">
            Hissedarlardan WhatsApp ile adres iste, sisteme kaydet ve seçtiğin teslimatlardan tek tıkla rota oluştur.
          </p>
        </div>
      </div>

      <div className="bg-navy-900 border border-navy-700/50 rounded-2xl shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center gap-3 p-4 border-b border-navy-700/50">
          <div className="flex bg-navy-800 rounded-xl p-1 border border-navy-700/50">
            <button
              onClick={() => setTab('list')}
              className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                tab === 'list' ? 'bg-forest-600 text-white shadow' : 'text-navy-300 hover:text-white'
              }`}
            >
              Teslim Listesi ({deliveries.length})
            </button>
            <button
              onClick={() => setTab('all')}
              className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                tab === 'all' ? 'bg-forest-600 text-white shadow' : 'text-navy-300 hover:text-white'
              }`}
            >
              Tüm Hissedarlar ({shareholders.filter(s => !deliveryPhoneSet.has(s.customerPhone)).length})
            </button>
          </div>

          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-navy-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ad, telefon veya adres ile ara..."
              className="w-full pl-9 pr-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-sm text-white placeholder-navy-500"
            />
          </div>

          {tab === 'list' && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-navy-800 border border-navy-700 rounded-lg text-sm text-white px-3 py-2"
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex flex-col border-b border-navy-700/50 bg-navy-900/80">
          {isSuperAdmin && systemSettings?.systemBranches?.length > 0 && (
            <div className="p-4 flex flex-wrap gap-2 items-center border-b border-navy-700/50 last:border-0">
               <span className="text-xs font-bold text-navy-400 mr-2 uppercase tracking-tight min-w-[90px]">Şube Filtresi:</span>
               <button 
                 onClick={() => setBranchFilter('all')} 
                 className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${branchFilter === 'all' ? 'bg-gold-500 text-navy-950 shadow-lg border border-gold-400' : 'bg-navy-800 text-gold-400 border border-gold-500/30 hover:bg-gold-500/20'}`}
               >
                 TÜMÜ
               </button>
               {systemSettings.systemBranches.map(branch => (
                  <button 
                    key={branch.id} 
                    onClick={() => setBranchFilter(branch.id)} 
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all uppercase ${branchFilter === branch.id ? 'bg-forest-600 text-white shadow-lg border border-forest-500' : 'bg-navy-800 text-forest-400 border border-forest-500/30 hover:bg-forest-500/20'}`}
                  >
                    {branch.name}
                  </button>
               ))}
            </div>
          )}
          {tab === 'list' && (
            <div className="p-4 flex flex-wrap gap-2 items-center border-b border-navy-700/50 last:border-0">
               <span className="text-xs font-bold text-navy-400 mr-2 uppercase tracking-tight min-w-[90px]">Teslim Tipi:</span>
               <button 
                 onClick={() => setDeliveryTypeFilter('all')} 
                 className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${deliveryTypeFilter === 'all' ? 'bg-blue-600 text-white shadow-lg border border-blue-500' : 'bg-navy-800 text-blue-400 border border-blue-500/30 hover:bg-blue-500/20'}`}
               >
                 TÜMÜ
               </button>
               <button 
                 onClick={() => setDeliveryTypeFilter('home')} 
                 className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${deliveryTypeFilter === 'home' ? 'bg-forest-600 text-white shadow-lg border border-forest-500' : 'bg-navy-800 text-forest-400 border border-forest-500/30 hover:bg-forest-500/20'}`}
               >
                 EVE TESLİM
               </button>
               <button 
                 onClick={() => setDeliveryTypeFilter('center')} 
                 className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${deliveryTypeFilter === 'center' ? 'bg-purple-600 text-white shadow-lg border border-purple-500' : 'bg-navy-800 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20'}`}
               >
                 MERKEZE TESLİM
               </button>
               <button 
                 onClick={() => setDeliveryTypeFilter('self')} 
                 className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${deliveryTypeFilter === 'self' ? 'bg-orange-600 text-white shadow-lg border border-orange-500' : 'bg-navy-800 text-orange-400 border border-orange-500/30 hover:bg-orange-500/20'}`}
               >
                 KENDİSİ ALACAK
               </button>
            </div>
          )}
        </div>

        {tab === 'list' && (
          <div className="p-4 border-b border-navy-700/50 bg-navy-900/60">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1">
                <label className="text-[10px] text-navy-400 block mb-1 uppercase tracking-wide">
                  Başlangıç noktası (opsiyonel)
                </label>
                <input
                  type="text"
                  value={startPoint}
                  onChange={(e) => setStartPoint(e.target.value)}
                  placeholder="Örn: Hasımoğlu Çiftliği, Gebze"
                  className="w-full bg-navy-800 border border-navy-700 rounded-lg text-sm text-white px-3 py-2 placeholder-navy-500"
                />
              </div>
              <div className="flex items-end gap-3">
                <div className="text-xs text-navy-300">
                  <div className="font-bold text-white text-lg leading-none">{selectedDeliveries.length}</div>
                  <div className="text-[10px] uppercase tracking-wide text-navy-500">seçili durak</div>
                </div>
                <button
                  onClick={handleSendToCourier}
                  disabled={selectedDeliveries.length === 0}
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-bold text-sm transition-all shadow-lg shadow-green-500/20 flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Kuryeye Gönder
                </button>
                <button
                  onClick={handleBuildRoute}
                  disabled={selectedDeliveries.length === 0}
                  className="px-4 py-2 bg-forest-600 hover:bg-forest-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-bold text-sm transition-all shadow-lg shadow-forest-500/20 flex items-center gap-2"
                >
                  <MapIcon className="w-4 h-4" />
                  Rota Oluştur
                </button>
              </div>
            </div>
            {selectedDeliveries.length > 22 && (
              <p className="text-[11px] text-amber-300 mt-2">
                ⚠ Çok adresli rotalar için Google Maps optimizasyonu zayıflayabilir. 2 gruba bölmenizi öneririz.
              </p>
            )}
          </div>
        )}

        <div className="p-4">
          {loading ? (
            <div className="text-center text-navy-400 py-12">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 opacity-50" />
              Yükleniyor...
            </div>
          ) : tab === 'list' ? (
            filteredDeliveries.length === 0 ? (
              <EmptyState
                title={deliveries.length === 0 ? 'Henüz teslim listesi boş' : 'Filtre ile eşleşen kayıt yok'}
                hint={deliveries.length === 0 ? '"Tüm Hissedarlar" sekmesinden ekleyebilirsiniz.' : 'Filtreyi veya aramayı temizleyin.'}
              />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredDeliveries.map(d => d.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {filteredDeliveries.map((d) => (
                      <DeliveryCard
                        key={d.id}
                        mode="delivery"
                        data={d}
                        selected={selectedIds.has(d.id)}
                        onSelectChange={(checked) => toggleSelect(d.id, checked)}
                        onClick={() => setActiveDelivery(d)}
                        branchLabel={branchName(d.branch)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )
          ) : filteredShareholders.length === 0 ? (
            <EmptyState
              title="Hissedar bulunamadı"
              hint={search ? 'Arama kriterini değiştirin.' : 'Henüz sistemde hissedar yok.'}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredShareholders.map((sh) => (
                <DeliveryCard
                  key={sh.customerPhone}
                  mode="shareholder"
                  data={{ ...sh, alreadyAdded: deliveryPhoneSet.has(sh.customerPhone) || adding === sh.customerPhone }}
                  onAdd={() => handleAddToList(sh)}
                  onCenterDelivery={() => handleCenterDelivery(sh)}
                  onSelfPickup={() => handleSelfPickup(sh)}
                  branchLabel={branchName(sh.branch)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <DeliveryDetailModal
        delivery={activeDeliveryLive}
        isOpen={!!activeDeliveryLive}
        onClose={() => setActiveDelivery(null)}
      />
    </div>
  );
}

function EmptyState({ title, hint }) {
  return (
    <div className="text-center py-12 text-navy-400">
      <AlertCircle className="w-8 h-8 opacity-30 mx-auto mb-3" />
      <p className="text-white font-bold text-sm">{title}</p>
      {hint && <p className="text-xs mt-1">{hint}</p>}
    </div>
  );
}
